import {
  type Event,
  type EventPayload,
  type RunRecord,
  type RunState,
  type Scenario,
  eventPayloadSchema,
  reduce,
} from '@orgsim/shared';
import { create } from 'zustand';
import { type ChannelMessage, openChannel, post } from './channel';
import { putRun } from './db';
import { examineeEvent, examineeRecord } from './examineeView';
import { focusSync, syncRun } from './sync';

type RunStore = {
  run: RunRecord | null;
  state: RunState | null;
  persistError: string | null;
  start: (scenario: Scenario) => Promise<void>;
  resume: (run: RunRecord) => void;
  dispatch: (payload: EventPayload) => void;
  undo: () => void;
  finish: () => Promise<void>;
  close: () => void;
};

let channel: BroadcastChannel | null = null;

// Register beforeunload listener once at module level.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (channel) {
      post(channel, { kind: 'closed' });
    }
  });
}

export const useRunStore = create<RunStore>((set, get) => {
  const persist = (run: RunRecord) => {
    putRun(run)
      .then(() => set({ persistError: null }))
      .catch((e: unknown) => set({ persistError: `Nicht gesichert: ${String(e)}` }));
  };

  const attach = (run: RunRecord) => {
    focusSync(run.id);
    // Tell any map window open on the previous run that it is abandoned before closing the
    // channel: closing silently leaves that window showing a stale map with no banner.
    if (channel) post(channel, { kind: 'closed' });
    channel?.close();
    channel = openChannel(run.id);
    channel.onmessage = (m: MessageEvent<ChannelMessage>) => {
      const msg = m.data;
      if (msg.kind === 'propose') get().dispatch(msg.payload);
      if (msg.kind === 'undo') get().undo();
      if (msg.kind === 'requestSync') {
        const current = get().run;
        if (current) {
          post(channel as BroadcastChannel, { kind: 'sync', run: examineeRecord(current) });
        }
      }
    };
  };

  const commit = (run: RunRecord) => {
    set({ run, state: reduce(run.scenario, run.events) });
    persist(run);
    syncRun(run);
  };

  return {
    run: null,
    state: null,
    persistError: null,

    async start(scenario) {
      const run: RunRecord = {
        id: crypto.randomUUID(),
        scenarioId: scenario.id,
        scenario: structuredClone(scenario),
        startedAt: Date.now(),
        events: [],
      };
      attach(run);
      commit(run);
    },

    resume(run) {
      attach(run);
      set({ run, state: reduce(run.scenario, run.events) });
    },

    dispatch(payload) {
      const run = get().run;
      if (!run || run.finishedAt) return;
      // Inject effects are event payloads too (files.ts validates them on import, but a
      // hand-edited scenario or a stale demo file could still carry a broken one), so every
      // payload - the dispatched one and any effects it triggers - is validated here, the
      // single entry point into the log. Nothing reaches the log unvalidated.
      const rawPayloads: unknown[] = [payload];
      if (payload.type === 'InjectTriggered') {
        const inject = run.scenario.injects.find((i) => i.id === payload.injectId);
        rawPayloads.push(...(inject?.effects ?? []));
      }
      const validated: EventPayload[] = [];
      for (const raw of rawPayloads) {
        const result = eventPayloadSchema.safeParse(raw);
        if (!result.success) {
          set({
            persistError: `Ereignis ungültig, nicht gespeichert: ${result.error.issues[0]?.message ?? 'unbekannter Fehler'}`,
          });
          return;
        }
        validated.push(result.data);
      }
      const mk = (p: EventPayload): Event => ({
        ...p,
        id: crypto.randomUUID(),
        time: Date.now() - run.startedAt,
      });
      const events: Event[] = validated.map(mk);
      const next = { ...run, events: [...run.events, ...events] };
      commit(next);
      if (channel) {
        for (const e of events) {
          const event = examineeEvent(e);
          if (event) post(channel, { kind: 'event', event });
        }
      }
    },

    undo() {
      const run = get().run;
      if (!run || run.events.length === 0 || run.finishedAt) return;
      const next = { ...run, events: run.events.slice(0, -1) };
      commit(next);
      if (channel) post(channel, { kind: 'sync', run: examineeRecord(next) });
    },

    async finish() {
      const run = get().run;
      if (!run) return;
      const next = { ...run, finishedAt: Date.now() };
      commit(next);
    },

    close() {
      if (channel) post(channel, { kind: 'closed' });
      channel?.close();
      channel = null;
      set({ run: null, state: null, persistError: null });
    },
  };
});
